import { useCallback, useState } from "react";
import { useSupabase } from "../auth/useSupabase";
import { gameService } from "../data/gameService";
import heic2any from "heic2any";
import { useNotification } from "../contexts/NotificationContext";

// Interface for attachment data including URL for display
interface AttachmentWithUrl {
  id: string;
  url: string;
  filename: string;
  filePath: string;
}

// Helper function to check if file is a HEIC image
export const isHeicImage = (file: File): boolean => {
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    file.name.toLowerCase().endsWith(".heic") ||
    file.name.toLowerCase().endsWith(".heif")
  );
};

// Helper function to convert HEIC to JPEG
export const convertHeicToJpeg = async (file: File): Promise<File> => {
  try {
    // Convert HEIC to Blob
    const jpegBlob = (await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.8,
    })) as Blob;

    // Original filename without extension
    const originalName = file.name.substring(0, file.name.lastIndexOf("."));

    // Create a new File object from the Blob
    return new File([jpegBlob], `${originalName}.jpg`, {
      type: "image/jpeg",
    });
  } catch (error) {
    console.error("HEIC conversion error:", error);
    throw new Error(`Failed to convert HEIC file: ${file.name}`);
  }
};

const useRoundAttachments = (args: {
  question1Id: string | null;
  question2Id: string | null;
}) => {
  const { question1Id, question2Id } = args;
  const { supabase } = useSupabase();

  // Loading states
  const [isUploading1, setIsUploading1] = useState<boolean>(false);
  const [isUploading2, setIsUploading2] = useState<boolean>(false);

  // For storing image attachments with their URLs for display
  const [attachments1, setAttachments1] = useState<AttachmentWithUrl[]>([]);
  const [attachments2, setAttachments2] = useState<AttachmentWithUrl[]>([]);

  const { showSnackbar } = useNotification();

  const loadAttachmentUrls = useCallback(async () => {
    const loadForQuestion = async (
      questionId: string | null,
    ): Promise<AttachmentWithUrl[]> => {
      if (!questionId) return [];

      const rows = await gameService.getQuestionAttachments(
        supabase,
        questionId,
      );
      if (rows.length === 0) return [];

      return Promise.all(
        rows.map(async (att) => {
          const { data, error } = await supabase.storage
            .from("dozen-attachments")
            .createSignedUrl(att.file_path, 3600);

          if (error) {
            console.error("Error getting signed URL:", error);
            return { id: att.id, url: "", filename: att.file_path, filePath: att.file_path };
          }

          return {
            id: att.id,
            url: data?.signedUrl || "",
            filename:
              att.file_path.length > 20
                ? `...${att.file_path.slice(-17)}`
                : att.file_path,
            filePath: att.file_path,
          };
        }),
      );
    };

    const [a1, a2] = await Promise.all([
      loadForQuestion(question1Id),
      loadForQuestion(question2Id),
    ]);

    setAttachments1(a1);
    setAttachments2(a2);
  }, [supabase, question1Id, question2Id]);

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    questionNumber: 1 | 2,
  ) => {
    const questionId = questionNumber === 1 ? question1Id : question2Id;
    if (!questionId) return;

    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Set uploading state for the appropriate question
    if (questionNumber === 1) {
      setIsUploading1(true);
    } else {
      setIsUploading2(true);
    }

    try {
      const currentCount =
        questionNumber === 1 ? attachments1.length : attachments2.length;
      let uploadedCount = 0;
      const filesArray = Array.from(files);

      // Process each file sequentially
      for (let i = 0; i < filesArray.length; i++) {
        let file = filesArray[i];
        let processedFile = file;

        // Convert HEIC files to JPEG
        if (isHeicImage(file)) {
          try {
            showSnackbar(
              `Converting ${file.name} from HEIC to JPEG...`,
              "info",
            );

            processedFile = await convertHeicToJpeg(file);

            showSnackbar(
              `Successfully converted ${file.name} to JPEG`,
              "success",
            );
          } catch (error) {
            showSnackbar(`Failed to convert HEIC file: ${file.name}`, "error");
            continue; // Skip this file and move to the next one
          }
        }

        // Create a unique filename
        const fileExt =
          processedFile.name.split(".").pop()?.toLowerCase() || "jpg";
        const fileName = `${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 15)}.${fileExt}`;
        const filePath = `attachments/${fileName}`;

        // Upload the file to Supabase storage
        const { error } = await supabase.storage
          .from("dozen-attachments")
          .upload(filePath, processedFile);

        if (error) {
          console.error("Error uploading file:", error);
          showSnackbar(
            `Error uploading ${processedFile.name}: ${error.message}`,
            "error",
          );
          continue;
        }

        // Record in question_attachments table
        await gameService.addQuestionAttachment(supabase, {
          question_id: questionId,
          file_path: filePath,
          sort_order: currentCount + uploadedCount,
        });

        uploadedCount++;
      }

      if (uploadedCount > 0) {
        // Refresh attachment URLs from DB
        await loadAttachmentUrls();

        if (uploadedCount === 1) {
          showSnackbar("1 file uploaded successfully", "success");
        } else {
          showSnackbar(
            `${uploadedCount} files uploaded successfully`,
            "success",
          );
        }
      }
    } catch (error) {
      console.error("Unexpected error during upload:", error);
      showSnackbar(
        `Upload failed: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        "error",
      );
    }

    // Reset uploading state
    if (questionNumber === 1) {
      setIsUploading1(false);
    } else {
      setIsUploading2(false);
    }

    // Clear the file input
    event.target.value = "";
  };

  const handleDeleteAttachment = async (
    attachmentId: string,
    questionNumber: 1 | 2,
  ) => {
    try {
      // Look up file_path from local state
      const attachmentsList =
        questionNumber === 1 ? attachments1 : attachments2;
      const attachment = attachmentsList.find((a) => a.id === attachmentId);

      // Remove from DB
      await gameService.removeQuestionAttachment(supabase, attachmentId);

      // Delete from storage if we have the path
      if (attachment?.filePath) {
        const { error } = await supabase.storage
          .from("dozen-attachments")
          .remove([attachment.filePath]);

        if (error) {
          console.error("Error deleting file from storage:", error);
        }
      }

      // Refresh from DB
      await loadAttachmentUrls();
    } catch (error) {
      console.error("Unexpected error during deletion:", error);
    }
  };

  return {
    handleDeleteAttachment,
    loadAttachmentUrls,
    attachments1,
    attachments2,
    handleFileUpload,
    isUploading1,
    isUploading2,
  };
};

export default useRoundAttachments;
